/* What the inline player is showing, and the "Up next in N" countdown after a video ends. */

export const UP_NEXT_SECONDS = 5;

/** What the inline player is doing with the current video (from the <video> element's events). */
export type PlayState = 'loading' | 'playing' | 'paused' | 'ended';

export const PLAY_STATE_LABELS: Record<PlayState, string> = {
  loading: 'Starting', playing: 'Now playing', paused: 'Paused', ended: 'Ended',
};

export class InlinePlayback {
  /** The classic play payload (selectedVideo, startSessionResponse, videoURL…) of the video in the player. */
  payload: any = null;
  state: PlayState = 'loading';
  upNext: any = null;
  upNextSeconds = 0;
  /** Video whose Play was clicked, while the sign-in / payment / start-session checks run (shows a spinner). */
  pendingId: any = null;
  private timer: any = null;

  constructor(private readonly autoplay: (video: any) => void) {}

  get currentId(): any {
    return this.payload?.selectedVideo?.id ?? null;
  }

  start(payload: any): void {
    this.cancelUpNext();
    this.pendingId = null;
    this.state = 'loading';
    this.payload = payload;
  }

  /** Marks `id` as starting while `run` (the classic play checks) is in progress. */
  async track<T>(id: any, run: () => Promise<T>): Promise<T> {
    const key = id ?? true;
    this.pendingId = key;
    try {
      return await run();
    } finally {
      if (this.pendingId === key) this.pendingId = null;
    }
  }

  /** Counts down, then plays `video` through the normal play flow (same checks, new session). */
  queueUpNext(video: any, seconds = UP_NEXT_SECONDS): void {
    this.cancelUpNext();
    if (!video) return;
    this.upNext = video;
    this.upNextSeconds = seconds;
    this.timer = setInterval(() => {
      this.upNextSeconds -= 1;
      if (this.upNextSeconds <= 0) this.playUpNext();
    }, 1000);
  }

  playUpNext(): void {
    const video = this.upNext;
    this.cancelUpNext();
    if (video) this.autoplay(video);
  }

  cancelUpNext(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.upNext = null;
    this.upNextSeconds = 0;
  }
}
